import type { RendererObject } from 'marked';

// https://marked.js.org/using_pro#renderer

// Block-level renderer methods
// space(token: Tokens.Space): string
// code(token: Tokens.Code): string
// blockquote(token: Tokens.Blockquote): string
// html(token: Tokens.HTML | Tokens.Tag): string
// heading(token: Tokens.Heading): string
// hr(token: Tokens.Hr): string
// list(token: Tokens.List): string
// listitem(token: Tokens.ListItem): string
// checkbox(token: Tokens.Checkbox): string
// paragraph(token: Tokens.Paragraph): string
// table(token: Tokens.Table): string
// tablerow(token: Tokens.TableRow): string
// tablecell(token: Tokens.TableCell): string

// Inline-level renderer methods
// strong(token: Tokens.Strong): string
// em(token: Tokens.Em): string
// codespan(token: Tokens.Codespan): string
// br(token: Tokens.Br): string
// del(token: Tokens.Del): string
// link(token: Tokens.Link): string
// image(token: Tokens.Image): string
// text(token: Tokens.Text | Tokens.Escape | Tokens.Tag): string

export function createMdRenderer(
  themeingClasses: Partial<Record<keyof RendererObject, string[]>>,
): RendererObject {
  const renderer: RendererObject = {
    heading({ tokens, depth }) {
      const text = this.parser.parseInline(tokens);
      const classes = themeingClasses.heading?.join(' ') || '';
      return `<h${depth} class="${classes}">${text}</h${depth}>`;
    },
    link({ href, title, text }) {
      const classes = themeingClasses.link?.join(' ') || '';
      const target = href.startsWith('http') ? '_blank' : '';
      return `<a href="${href}" title="${title || ''}" target="${target}" class="${classes}">${text}</a>`;
    },
    strong({ tokens }) {
      const text = this.parser.parseInline(tokens);
      const classes = themeingClasses.strong?.join(' ') || '';
      return `<strong class="${classes}">${text}</strong>`;
    },
    em({ tokens }) {
      const text = this.parser.parseInline(tokens);
      const classes = themeingClasses.em?.join(' ') || '';
      return `<em class="${classes}">${text}</em>`;
    },
    br() {
      return '<br />';
    },
    list(token) {
      const classes = themeingClasses.list?.join(' ') || '';
      const type = token.ordered ? 'ol' : 'ul';
      const body = token.items.map((item) => this.listitem(item)).join('');
      return `<${type} class="${classes}">${body}</${type}>`;
    },
    listitem(token) {
      const classes = themeingClasses.listitem?.join(' ') || '';
      const text = this.parser.parseInline(token.tokens);
      return `<li class="${classes}">${text}</li>`;
    },
    paragraph(token) {
      const text = this.parser.parseInline(token.tokens);
      const classes = themeingClasses.paragraph?.join(' ') || '';
      return `<p class="${classes}">${text}</p>`;
    },
  };

  Object.keys(themeingClasses).forEach((key) => {
    if (!(key in renderer)) {
      console.error(`Theming class for "${key}" has to be implemented in the renderer to work.`);
    }
  });

  return renderer;
}
